import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { invalidateVersionHistoryQueries } from "./versionQueryUtils";
import { UpdateCellRequest, SmartCell, unwrapSmartTableEntityPayload } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export const useUpdateCell = () => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const queryClient = useQueryClient();

  return useMutation<
    SmartCell,
    unknown,
    { tableId: string; sheetId: string; cellId: string } & UpdateCellRequest
  >({
    mutationFn: async ({ tableId, sheetId, cellId, ...payload }) => {
      const response = await fetch(ENDPOINTS.smart_table_cell(wid, tableId, sheetId, cellId), {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(payload),
      });
      const responsePayload = await handleResponse(response);
      return unwrapSmartTableEntityPayload<SmartCell>(responsePayload, "cell");
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "smart-tables",
          wid,
          variables.tableId,
          variables.sheetId,
          "cells",
          variables.cellId,
        ],
      });
      invalidateVersionHistoryQueries(queryClient, wid, variables.tableId, variables.sheetId);
    },
  });
};
